import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs';
import {MftAllowListDataService} from "@mft/default-dashboard/services/mft-allow-list-data.service";
import {DebtorKey, LeaseKey, TenantKey} from "@pma-entity/pma-entity-types";

/**
 * Ensures the requested dashboard is within the user's allow lists.
 *
 * N.B. using client side lists to do this isn't 'proper' app security of
 * course, so individual API end points perform server side checks too.
 *
 * However, redirecting the user if an invalid URL is requested provides
 * both semblance of data protection as well as the required UX to inform
 * the user in case a bookmark for a different login was used by mistake.
 *
 * No information/distinction is supplied to indicate an entity that exists
 * and is not permitted (401) vs an entity that does not exist at all (404).
 * Both are classes here as 'invalid' (400).
 */
@Injectable({
  providedIn: 'root',
})
export class MftInvalidEntityRouteGuard {

  constructor(
    private readonly router: Router,
    private readonly allowListProvider: MftAllowListDataService) {}

  canActivate(route: ActivatedRouteSnapshot,
              state: RouterStateSnapshot): Observable<boolean> {

    const tenantRef = route.params['tenantId'];
    const leaseRef = route.params['leaseId'];
    const debtorUri = route.params['debtorId'];

    return this.allowListProvider.getForCurrentUser().pipe(
      map(mftAllowLists => {
        let isValid = false;
        if (tenantRef !== undefined) {
          isValid = mftAllowLists?.AllowedTenants?.some((key: TenantKey) => key.Ref == tenantRef);
        } else if (leaseRef !== undefined) {
          isValid = mftAllowLists?.AllowedLeases?.some((key: LeaseKey) => key.Ref == leaseRef);
        } else if (debtorUri !== undefined) {
          isValid = mftAllowLists?.AllowedDebtors?.some((key: DebtorKey) => key.Uri == debtorUri);
        }
        if (isValid) {
          return true;
        } else {
          this.router.navigate(['/invalid-entity'],
            { queryParams: { 'previouslyRequestedUrl': state.url } });
          return false;
        }
      })
    );
  }

}
